import React, { useState, useContext, useRef } from "react";
import { useAtom } from "jotai";
import { StoreContext } from "../context/StoreContext";
import {
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogContent,
    Grid,
    Typography,
    Button
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import useHandleClick from "../utils/Common/ClickHandler";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AlarmIcon from "@mui/icons-material/Alarm";
import SettingsIcon from "@mui/icons-material/Settings";
import UpdateIcon from "@mui/icons-material/Update";
import BlockIcon from "@mui/icons-material/Block";
import OrderIcon from "@mui/icons-material/Assignment";
import UsersIcon from "@mui/icons-material/Person";
import UsersIconAdmin from "@mui/icons-material/PeopleAlt";
import ReportsIcon from "@mui/icons-material/BarChart";
import IntegrationsIcon from "@mui/icons-material/Share";
import LaunchBookingFlowIcon from "@mui/icons-material/ArrowOutward";
import ServicesIcon from "@mui/icons-material/HomeRepairService";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DiscountIcon from "@mui/icons-material/Discount";
import PackageIcon from "../utils/CommonComponents/PackageIcon";
import RegionalPricingIcon from "../utils/CommonComponents/RegionalPricingIcon";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CompanyIcon from "@mui/icons-material/AccountBalance";
import ClientsIcon from "../utils/CommonComponents/ClientsIcon";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import { useTheme, ThemeProvider } from "@mui/material/styles";
import { DrawerItemsTheme } from "../styles/DrawerItemsTheme";
import { ClientDrawerItemsTheme } from "../styles/ClientDrawerItemsTheme";

import { useLocation, useNavigate } from "react-router-dom";
import {
    mbxUserAtom,
    isMbxUserAdmin,
    isMbxUserProvider,
    isMbxUserClient,
    showCompanySettingsModalAtom,
    isMbxUserMbxAdmin,
    isMbxUserManager,
    isMbxUserVerifiedStaff,
    mainNavBarHeightAtom
} from "../atoms/atoms";
import { openUserDialogAtom, userModalTabValue } from "../atoms/usersTable";
import {
    selectedClientAtom,
    selectedProviderAtom
} from "../atoms/bookingsTable";
import { FlagsContext } from "../context/flags";

import { graphql, graphqlOperation } from "../modules/AmplifyServices";
import * as queries from "../graphql/queries";
import isFeatureGated from "../modules/FeatureGatingModule";

const NewDrawerItems = (props) => {
    const theme = useTheme();
    const [overflow, setOverflow] = useState("hidden");
    const featureFlags = useContext(FlagsContext);
    const { displayNewBookingsFlag, displayNewServicesFlag } =
        featureFlags || {};
    const location = useLocation();
    const navigate = useNavigate();
    const handleClick = useHandleClick(navigate);
    const { actions } = useContext(StoreContext);

    const [openMenu, setOpenMenu] = useState(false);
    const [myProfileDropDown, setMyProfileDropDown] = useState(false);
    const [bookNowDropDown, setBookNowDropDown] = useState(false);
    const [openDisabledBookingFlowModal, setOpenDisabledBookingFlowModal] =
        useState(false);
    const [openUserDialog, setOpenUserDialog] = useAtom(openUserDialogAtom);
    const [showCompanySettingsModal, setShowCompanySettingsModal] = useAtom(
        showCompanySettingsModalAtom
    );

    const [tabValue, setTabValue] = useAtom(userModalTabValue);

    const [mbxUser] = useAtom(mbxUserAtom);
    const [isAdminLoggedIn] = useAtom(isMbxUserAdmin);
    const [isManagerLoggedIn] = useAtom(isMbxUserManager);
    const [isVerifiedStaffLoggedIn] = useAtom(isMbxUserVerifiedStaff);
    const [isMbxAdminLoggedIn] = useAtom(isMbxUserMbxAdmin);
    const [isProviderLoggedIn] = useAtom(isMbxUserProvider);
    const [isClientLoggedIn] = useAtom(isMbxUserClient);
    const [mainNavBarHeight] = useAtom(mainNavBarHeightAtom);
    const [, setSelectedClient] = useAtom(selectedClientAtom);
    const [, setSelectedProvider] = useAtom(selectedProviderAtom);

    let url;
    if (mbxUser && mbxUser.company) {
        url = "https://" + mbxUser.company.subdomain;
    }

    const Titles = {
        dashboard: "Dashboard",
        providers: "Users",
        customers: "Clients",
        orders: isVerifiedStaffLoggedIn ? "Orders" : "My orders",
        billing: isAdminLoggedIn ? "Billing" : "Billing Charges",
        bookings: "Bookings",
        newBookings: "New bookings",
        availability: "Availability",
        services: "Services",
        newServices: "New services",
        locations: "Locations",
        packages: "Packages",
        promocodes: "Promo codes",
        regionalPricing: "Regional pricing",
        users: "Users",
        misc: "Misc. Billing",
        booknow: isVerifiedStaffLoggedIn ? "Online booking" : "Book now",
        calendar: "Calendar",
        myCompany: "Settings",
        addCharges: "Additional Charges",
        admin: "Admin",
        report: "Reports",
        companies: "Companies",
        profile: "Profile",
        billingInfo: "Billing Info",
        providerSchedule: "My Schedules",
        blockedTime: "Blocked time",
        integration: "Integrations",
        updateEmail: "Update Email",
        settings: "Settings"
    };

    const getMenuItems = () => {
        if (isAdminLoggedIn) {
            let menus = [
                {
                    title: Titles.dashboard,
                    route: "/dashboard",
                    icon: <DashboardIcon />
                },
                {
                    title: Titles.orders,
                    route: "/orders",
                    icon: <ShoppingCartIcon />
                },
                {
                    title: Titles.bookings,
                    route: "/bookings",
                    icon: <OrderIcon />
                },
                // Conditionally render newBookings item based on displayNewBookingsFlag
                ...(displayNewBookingsFlag
                    ? [
                          {
                              title: Titles.newBookings,
                              onClick: () => {
                                  setSelectedProvider(null);
                                  setSelectedClient(null);
                                  navigate("/new-bookings/today");
                              },
                              route: "/new-bookings/today",
                              selected:
                                  location.pathname.includes("new-bookings"),
                              icon: <OrderIcon />
                          }
                      ]
                    : []),
                {
                    title: Titles.calendar,
                    route: "/calendar",
                    icon: <InsertInvitationIcon />
                },
                {
                    title: Titles.customers,
                    route: "/clients",
                    icon: <ClientsIcon />
                },
                {
                    title: Titles.users,
                    route: "/users",
                    icon: <UsersIconAdmin />,
                    selected:
                        (location.pathname.includes("/users") &&
                            !showCompanySettingsModal) ||
                        (location.pathname.includes("/users") &&
                            openUserDialog &&
                            !showCompanySettingsModal)
                },
                {
                    title: Titles.services,
                    route: "/services",
                    icon: <ServicesIcon />
                },
                // Conditionally render newServices item based on displayNewServicesFlag
                ...(displayNewServicesFlag
                    ? [
                          {
                              title: Titles.newServices,
                              route: "/new-services/",
                              selected:
                                  location.pathname.includes("new-services"),
                              icon: <ServicesIcon />
                          }
                      ]
                    : []),
                {
                    title: Titles.settings,
                    onClick: () => {
                        actions.setMode("Edit");
                        actions.setId(mbxUser.company?.id);
                        setShowCompanySettingsModal(true);
                    },
                    selected: showCompanySettingsModal,
                    icon: <SettingsIcon />
                },
                {
                    title: Titles.report,
                    route: "/reports",
                    icon: <ReportsIcon />
                },
                {
                    title: Titles.integration,
                    route: "/integrations",
                    icon: <IntegrationsIcon />,
                    disabled: isFeatureGated(
                        props.subscriptionLevel,
                        "Integrations"
                    )
                },
                {
                    title: Titles.booknow,
                    route: url,
                    onClick: (e) => launchBookingHandler(mbxUser, url, e),
                    icon: (
                        <LaunchBookingFlowIcon
                            sx={{ transform: "scale(1.2)" }}
                        />
                    )
                }
            ];
            if (isMbxAdminLoggedIn)
                menus.push({
                    title: Titles.companies,
                    route: "/companies",
                    icon: <CompanyIcon />
                });
            return menus;
        }
        if (isManagerLoggedIn) {
            let menus = [
                {
                    title: Titles.orders,
                    route: "/orders",
                    icon: <ShoppingCartIcon />
                },
                {
                    title: Titles.bookings,
                    route: "/bookings",
                    icon: <OrderIcon />
                },
                ...(displayNewBookingsFlag
                    ? [
                          {
                              title: Titles.newBookings,
                              onClick: () => {
                                  setSelectedProvider(null);
                                  setSelectedClient(null);
                                  navigate("/new-bookings/today");
                              },
                              route: "/new-bookings/today",
                              selected:
                                  location.pathname.includes("new-bookings"),
                              icon: <OrderIcon />
                          }
                      ]
                    : []),
                {
                    title: Titles.calendar,
                    route: "/calendar",
                    icon: <InsertInvitationIcon />
                },
                {
                    title: Titles.customers,
                    route: "/clients",
                    icon: <ClientsIcon />
                },
                {
                    title: Titles.users,
                    route: "/users",
                    icon: <UsersIconAdmin />,
                    selected:
                        location.pathname.includes("/users") ||
                        (location.pathname.includes("/users") && openUserDialog)
                },
                {
                    title: Titles.report,
                    route: "/reports",
                    icon: <ReportsIcon />
                },
                {
                    title: Titles.booknow,
                    onClick: (e) => launchBookingHandler(mbxUser, url, e),
                    icon: (
                        <LaunchBookingFlowIcon
                            sx={{ transform: "scale(1.2)" }}
                        />
                    )
                }
            ];
            return menus;
        } else if (isProviderLoggedIn) {
            return [
                {
                    title: Titles.bookings,
                    route: "/bookings",
                    icon: <OrderIcon />
                },
                // Conditionally render newBookings item based on displayNewBookingsFlag
                ...(displayNewBookingsFlag
                    ? [
                          {
                              title: Titles.newBookings,
                              onClick: () => {
                                  setSelectedProvider(null);
                                  setSelectedClient(null);
                                  navigate("/new-bookings/today");
                              },
                              route: "/new-bookings/today",
                              selected:
                                  location.pathname.includes("new-bookings") &&
                                  !openUserDialog,
                              icon: <OrderIcon />
                          }
                      ]
                    : []),
                {
                    title: Titles.calendar,
                    route: "/calendar",
                    icon: <InsertInvitationIcon />
                },
                {
                    title: Titles.profile,
                    onClick: () => {
                        setTabValue(0);
                        setOpenUserDialog(true);
                    },
                    selected: openUserDialog && tabValue === 0,
                    icon: <UsersIcon />
                },
                {
                    title: Titles.availability,
                    onClick: () => {
                        setTabValue(1);
                        setOpenUserDialog(true);
                    },
                    selected: openUserDialog && tabValue === 1,
                    icon: <UpdateIcon />
                },
                {
                    title: Titles.blockedTime,
                    onClick: () => {
                        setTabValue(2);
                        setOpenUserDialog(true);
                    },
                    selected: openUserDialog && tabValue === 2,
                    icon: <BlockIcon />
                }
            ];
        }
        return [];
    };

    const menuItems = getMenuItems();

    const fetchPublicSiteData = async (mbxUser) => {
        const companyId = mbxUser.company?.id;
        if (mbxUser.id) {
            let publicSiteData;
            try {
                publicSiteData = await graphql(
                    graphqlOperation(queries.publicsiteByCompany, {
                        companyId: companyId
                    })
                );
            } catch (err) {
                console.log(
                    "Dashboard Form | fetchPublicSiteData - Error in getting PublicSiteData"
                );
            }
            return publicSiteData;
        }
    };

    async function launchBookingHandler(mbxUser, url, event) {
        if (event?.metaKey || event?.ctrlKey) {
            // If Cmd or Ctr + click let the browser handle the navigation (e.g., open in a new tab)
            return;
        }

        window.open(url, "_blank").focus();
    }

    const customizeBookingHandler = () => {
        actions.setPage("BFSettings");
    };

    const DisplayProfile = (
        <ListItemButton
            id={"users-list"}
            component="a"
            href={"/profile"}
            onClick={(e) =>
                handleClick(e, "/profile", () => {
                    setMyProfileDropDown((prevState) => !prevState);
                })
            }
            selected={location.pathname.includes("profile")}
        >
            <ListItemIcon>
                <UsersIcon />
            </ListItemIcon>
            <ListItemText primary={Titles.profile} />
        </ListItemButton>
    );

    const DisplayBookings = (
        <ListItemButton
            id={"bookings-list"}
            component="a"
            href={"/bookings"}
            onClick={(e) => handleClick(e, "/bookings")}
            selected={location.pathname.includes("/bookings")}
        >
            <ListItemIcon>
                <OrderIcon />
            </ListItemIcon>
            <ListItemText primary={Titles.bookings} />
        </ListItemButton>
    );

    const DisplayBookNow = (
        <>
            {openDisabledBookingFlowModal && (
                <Dialog
                    open={openDisabledBookingFlowModal}
                    onClose={() => {
                        setOpenDisabledBookingFlowModal(false);
                    }}
                    maxWidth={"sm"}
                >
                    <DialogContent
                        sx={{
                            padding: "1.5rem 0px",
                            maxWidth: "404px"
                        }}
                    >
                        <Grid
                            container
                            style={{
                                justifyContent: "center",
                                gap: "13px"
                            }}
                        >
                            <Typography style={{ fontSize: "22px" }}>
                                Action Required
                            </Typography>
                            <Typography
                                style={{
                                    textAlign: "center",
                                    maxWidth: "84%"
                                }}
                            >
                                Please complete setting up your booking flow
                                before launching.
                            </Typography>
                            <Grid>
                                <Button
                                    variant="filled"
                                    onClick={customizeBookingHandler}
                                >
                                    Go to Settings
                                </Button>
                                <Button
                                    onClick={() =>
                                        setOpenDisabledBookingFlowModal(false)
                                    }
                                >
                                    Close
                                </Button>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </Dialog>
            )}
            <ListItemButton
                id={"book-now"}
                component="a"
                href={url}
                onClick={(e) => launchBookingHandler(mbxUser, url, e)}
            >
                <ListItemIcon>
                    <AlarmIcon />
                </ListItemIcon>
                <ListItemText primary={Titles.booknow} />
            </ListItemButton>
        </>
    );

    // Services menu item
    const [openServicesSubMenu, setOpenServicesSubMenu] = useState(false);
    const hoveringChevronRef = useRef(false);
    const hoveringSubMenuRef = useRef(false);
    const handleMouseEnterChevron = () => {
        hoveringChevronRef.current = true;
        setOverflow("unset");
        setTimeout(() => {
            if (hoveringChevronRef.current || hoveringSubMenuRef.current) {
                setOpenServicesSubMenu(true);
            }
        }, 200);
    };
    const handleMouseLeaveChevron = () => {
        setOverflow("hidden");
        hoveringChevronRef.current = false;

        if (!hoveringChevronRef.current && !hoveringSubMenuRef.current) {
            setOpenServicesSubMenu(false);
        }
    };
    const handleMouseEnterSubMenu = () => {
        setOverflow("unset");
        hoveringSubMenuRef.current = true;
        setOpenServicesSubMenu(true);
    };
    const handleMouseLeaveSubMenu = () => {
        setOverflow("hidden");
        hoveringSubMenuRef.current = false;
        hoveringChevronRef.current = false;

        if (!hoveringChevronRef.current && !hoveringSubMenuRef.current) {
            setOpenServicesSubMenu(false);
        }
    };

    const DisplayServices = (
        <>
            <ListItemButton
                disablePadding
                id="Services"
                component="a"
                href={"/services"}
                selected={
                    location.pathname.includes("services") &&
                    !showCompanySettingsModal
                }
                onClick={(e) => handleClick(e, "/services")}
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <ListItemIcon>
                    <ServicesIcon />
                </ListItemIcon>
                <ListItemText primary={Titles.services} />

                {openMenu && (
                    <ListItemIcon
                        sx={{
                            top: "8px",
                            cursor: "pointer",
                            marginLeft: "82px",
                            marginRight: "0px",
                            paddingRight: "27px"
                        }}
                        onMouseEnter={handleMouseEnterChevron}
                        onMouseLeave={handleMouseLeaveChevron}
                    >
                        <ArrowRightIcon />
                    </ListItemIcon>
                )}
            </ListItemButton>

            {openServicesSubMenu && (
                <List
                    onMouseEnter={handleMouseEnterSubMenu}
                    onMouseLeave={handleMouseLeaveSubMenu}
                    sx={{
                        width: "fit-content",
                        position: "absolute",
                        zIndex: 1,
                        top: "0",
                        left: "100%",
                        height: "29vh",
                        maxHeight: "232px",
                        marginLeft: "-8px",
                        padding: "8px 0px",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)"
                    }}
                >
                    <ListItemButton
                        variant="submenu"
                        id={"Locations"}
                        component="a"
                        href={"/services/locations"}
                        onClick={(e) =>
                            handleClick(e, "/services/locations", () => {
                                setOpenServicesSubMenu(false);
                                setOpenMenu(false);
                            })
                        }
                    >
                        <ListItemIcon>
                            <LocationOnIcon />
                        </ListItemIcon>
                        <ListItemText primary={Titles.locations} />
                    </ListItemButton>
                    <ListItemButton
                        variant="submenu"
                        id={"Packages"}
                        component="a"
                        href={"/services/packages"}
                        onClick={(e) =>
                            handleClick(e, "/services/packages", () => {
                                setOpenServicesSubMenu(false);
                                setOpenMenu(false);
                            })
                        }
                    >
                        <ListItemIcon>
                            <PackageIcon stroke={theme.palette.text.main} />
                        </ListItemIcon>
                        <ListItemText>{Titles.packages}</ListItemText>
                    </ListItemButton>
                    <ListItemButton
                        variant="submenu"
                        id={"Promocodes"}
                        component="a"
                        href={"/services/promo-codes"}
                        onClick={(e) =>
                            handleClick(e, "/services/promo-codes", () => {
                                setOpenServicesSubMenu(false);
                                setOpenMenu(false);
                            })
                        }
                    >
                        <ListItemIcon>
                            <DiscountIcon />
                        </ListItemIcon>
                        <ListItemText>{Titles.promocodes}</ListItemText>
                    </ListItemButton>
                    <ListItemButton
                        variant="submenu"
                        id={"Regional pricing"}
                        component="a"
                        href={"/services/regional-pricing"}
                        onClick={(e) =>
                            handleClick(e, "/services/regional-pricing", () => {
                                setOpenServicesSubMenu(false);
                                setOpenMenu(false);
                            })
                        }
                    >
                        <ListItemIcon>
                            <RegionalPricingIcon />
                        </ListItemIcon>
                        <ListItemText>{Titles.regionalPricing}</ListItemText>
                    </ListItemButton>
                </List>
            )}
        </>
    );

    const openedDrawerWidth = isVerifiedStaffLoggedIn ? "207px" : "195px";
    const listHeight = isVerifiedStaffLoggedIn ? "90%" : "45%";

    return isClientLoggedIn ? (
        <ThemeProvider
            theme={ClientDrawerItemsTheme({
                theme,
                openMenu,
                overflow,
                openedDrawerWidth,
                listHeight,
                showCompanySettingsModal,
                openUserDialog,
                mainNavBarHeight
            })}
        >
            <MuiDrawer variant="permanent" open={openMenu}>
                <List>
                    {DisplayBookNow}
                    {DisplayBookings}
                    {DisplayProfile}
                </List>
            </MuiDrawer>
        </ThemeProvider>
    ) : (
        <ThemeProvider
            theme={DrawerItemsTheme({
                theme,
                openMenu,
                overflow,
                openedDrawerWidth,
                listHeight,
                showCompanySettingsModal,
                openUserDialog,
                mainNavBarHeight
            })}
        >
            <MuiDrawer
                variant="permanent"
                open={openMenu}
                onMouseEnter={() => setOpenMenu(true)}
                onMouseLeave={() => setOpenMenu(false)}
            >
                <List>
                    {menuItems.map((item, index) => (
                        <ListItem key={index} disablePadding>
                            {item.title === Titles.services ? (
                                DisplayServices
                            ) : (
                                <ListItemButton
                                    component="a"
                                    href={item.route}
                                    variant={
                                        item.disabled
                                            ? "disabled-styling"
                                            : "button"
                                    }
                                    onClick={(e) => {
                                        handleClick(
                                            e,
                                            item.route,
                                            item.onClick
                                        );
                                        setOpenMenu(false);
                                    }}
                                    selected={
                                        item.selected
                                            ? item.selected
                                            : location.pathname ===
                                                  item.route &&
                                              !showCompanySettingsModal &&
                                              !openUserDialog
                                    }
                                >
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.title} />
                                </ListItemButton>
                            )}
                        </ListItem>
                    ))}
                </List>
            </MuiDrawer>
        </ThemeProvider>
    );
};

export default NewDrawerItems;
